import React, { useState, useEffect, useCallback } from 'react';
import { MenuItem, Typography, FormControl, Select } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SmallError } from '../../../../icons/SmallError';
import useDevices from '../../../../hooks/useDevices/useDevices';

const labels = {
  audioinput: {
    audioLevelText: 'Input level',
    headerText: 'Microphone',
  },
  audiooutput: {
    audioLevelText: 'Output level',
    headerText: 'Speaker',
  },
};

const StyledFormControl = styled(FormControl)({
  margin: '0.5em 0 1em',
});

const DeviceLabelContainer = styled('div')({
  margin: '0.8em 0',
});

const StyledSelect = styled(Select)({
  height: '36px',
});

const ErrorContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '1em',
  '& svg': {
    marginRight: '0.3em',
  },
});

interface AudioDeviceProps {
  disabled: boolean;
  kind: 'audioinput' | 'audiooutput';
  onDeviceChange: (value: string) => void;
  setDeviceError: (value: string) => void;
  error?: string;
}

export function AudioDevice({ disabled, kind, onDeviceChange, setDeviceError, error }: AudioDeviceProps) {
  const devices = useDevices();
  const audioDevices = kind === 'audiooutput' ? devices.audioOutputDevices : devices.audioInputDevices;
  const [selectedDevice, setSelectedDevice] = useState('');
  const { headerText } = labels[kind];
  const noAudioRedirect = !Audio.prototype.setSinkId && kind === 'audiooutput';

  const updateSelectedDevice = useCallback(
    (value: string) => {
      onDeviceChange(value);
      setSelectedDevice(value);
      setDeviceError('');
    },
    [onDeviceChange, setSelectedDevice, setDeviceError]
  );

  useEffect(() => {
    const hasSelectedDevice = audioDevices.some((device) => device.deviceId === selectedDevice);
    if (audioDevices.length && !hasSelectedDevice) {
      updateSelectedDevice(audioDevices[0].deviceId);
    }
  }, [audioDevices, devices, selectedDevice, updateSelectedDevice]);

  return (
    <>
      <Typography variant="subtitle2">
        <strong>{headerText}</strong>
      </Typography>

      {noAudioRedirect && (
        <DeviceLabelContainer>
          <Typography variant="body1">System Default Audio Output</Typography>
        </DeviceLabelContainer>
      )}

      {!noAudioRedirect && (
        <StyledFormControl variant="outlined" disabled={disabled} fullWidth>
          <StyledSelect
            value={selectedDevice}
            onChange={(e) => updateSelectedDevice(e.target.value as string)}
          >
            {audioDevices.map((device) => (
              <MenuItem value={device.deviceId} key={device.deviceId}>
                {device.label}
              </MenuItem>
            ))}
          </StyledSelect>
        </StyledFormControl>
      )}

      {error && (
        <ErrorContainer>
          <SmallError />
          <Typography variant="subtitle2" color="error">
            {error === 'No audio detected' ? 'No audio detected.' : 'Unable to connect.'}
          </Typography>
        </ErrorContainer>
      )}
    </>
  );
}

export default React.memo(AudioDevice);
